/**
 * taxPreviewInputs.js — what the Formula List's "ทดลองคำนวณ" panel ASKS FOR, and what it SENDS.
 *
 * PURE ESM: no Vue, no DOM, no I/O → importable by plain Node, which is the point. This logic decides
 * whether an admin can preview a path formula at all, and it used to live inline in TaxCalculateList.vue
 * where nothing could run it. Two of the three defects it now guards against were invisible for exactly
 * that reason.
 *
 * ── The two rules everything here follows ────────────────────────────────────────────────────
 *
 * 1. AN EXPRESSION IS SCANNED THROUGH THE MASK, NEVER RAW. A text literal's content is data and a
 *    PATH is ONE name — `INCOME_40_5.subItems[].items[].amount` names four things that are not
 *    variables. A raw identifier sweep reads it as four and mints input fields called
 *    "จำนวนเงิน (items)" and "จำนวนเงิน (amount)": numbers the formula cannot consume, asked for
 *    instead of the rows it actually needs. (Same two-step, same order, as the backend's
 *    TaxCalcEngineBAL.ReferenceScan.)
 *
 * 2. WHAT THE FORMULA READS IS WHAT THE PANEL OFFERS. A formula declares nothing about what it is
 *    "for" — only what the admin dragged into it. A rule reached by a `[]` PATH is entered as NESTED
 *    ROWS (subItems, each with its own asset category and therefore its own เหมา rate, each holding
 *    items); a rule reached by its scalar bridge var is one amount. Offering the wrong one is how a
 *    §40(5) formula previewed ฿0 with nothing on screen to explain it: the engine walked
 *    `subItems[].items[]`, found no rows, and an empty collection contributes 0 — correctly, silently.
 */
import { tokenize, pathRoot } from './formulaGraph.js'
import { referenceScan, EXPENSE_40_5_OPTIONS } from './taxCalculateStore.js'

/** The one option code that means "หักตามจริง" — the only one with no เหมา rate. */
export const ACTUAL_OPTION = 'ACTUAL'

// Names that are never an INPUT: the engine derives them, so asking the admin for one would let them
// contradict the very formulas being previewed.
const CATALOG_GLOBALS = new Set([
  'gross_income', 'total_income', 'expense_deduction', 'total_deductions', 'total_allowances', 'net_income',
])
const PERSONAL_NUMERIC = new Set(['age', 'withholding_tax'])
const PERSONAL_NON_NUMERIC = new Set([
  'sex', 'marital_status', 'spouse_info_status', 'form_type', 'tax_year', 'date_of_birth', 'nationality',
])
// `<rule_code_lower>_rate` — the engine's per-rule เหมา-rate bridge var. Referencing one is what makes
// a formula rate-driven, so it (not a binding) is what surfaces the expense-option picker.
const RATE_BRIDGE_RE = /^([a-z][a-z0-9_]*)_rate$/
const IDENT_RE = /[A-Za-z_][A-Za-z0-9_]*/g
const BRIDGE_NAME_RE = /^[a-z][a-z0-9_]*$/
// TRUE / FALSE are KEYWORDS of the grammar (IFS's always-matching last branch is `IFS(…, TRUE, 0)`),
// not names. Skipped for the same reason function names are: `true` is a legal lower_snake รหัสสูตร, so
// a formula that happened to be called that would otherwise gain a phantom dependency from every
// single IFS formula in the version.
const BOOL_KEYWORD_RE = /^(?:TRUE|FALSE)$/i

/**
 * Every VARIABLE name in `expression`, function names excluded — read off the MASKED text (rule 1).
 * @param {string} expression
 * @param {Set<string>|string[]} [functionNames] upper-cased function names to skip
 * @returns {string[]} in first-seen order, duplicates included (callers de-dupe by meaning)
 */
export function identifiersOf(expression, functionNames) {
  const fns = functionNames instanceof Set
    ? functionNames
    : new Set((Array.isArray(functionNames) ? functionNames : []).map((f) => String(f).toUpperCase()))
  const scanned = referenceScan(String(expression == null ? '' : expression))
  const out = []
  let m
  IDENT_RE.lastIndex = 0
  while ((m = IDENT_RE.exec(scanned)) !== null) {
    if (fns.has(m[0].toUpperCase()) || BOOL_KEYWORD_RE.test(m[0])) continue
    out.push(m[0])
  }
  return out
}

/**
 * The rule codes (lower-cased) that `formulas` read with an ITERATING `[]` path — one per distinct
 * path ROOT that carries a `[]`.
 *
 * Through the real tokenizer, never a regex: a text literal spelling `A.b[].c` is data, not a path.
 * A dotted path with NO `[]` is one value, not a row list, so it does not qualify.
 * @param {Array<{expression?:string}>} formulas
 * @returns {Set<string>}
 */
export function iteratedRoots(formulas) {
  const roots = new Set()
  ;(Array.isArray(formulas) ? formulas : []).forEach((f) => {
    let toks
    try { toks = tokenize(String((f && f.expression) || '')) } catch (_err) { return }
    toks.forEach((t) => {
      if (!t || t.type !== 'path') return
      if (!(t.segments || []).some((g) => g.iter)) return
      const root = pathRoot(t.value)
      if (root) roots.add(root.toLowerCase())
    })
  })
  return roots
}

/**
 * The input fields the panel must render for `closure` (the target formula plus everything it pulls
 * in). Order is meaningful: iterated rules first, because their row list REPLACES both scalar fields
 * the rule would otherwise get.
 *
 * @param {Array<Object>} closure    formulas, each `{ expression, outputVar }`
 * @param {Object} opts
 * @param {string[]} [opts.constKeys]   this version's constant keys (never inputs — they are authored)
 * @param {string[]} [opts.outputVars]  every formula's รหัสสูตร (computed, never entered)
 * @param {string[]} [opts.functionNames] the grammar's function names, to skip in the identifier scan
 * @returns {Array<{kind:string, model:string, label:string, ruleCode?:string, key?:string, default?:string}>}
 */
export function derivePreviewFields(closure, opts = {}) {
  const list = Array.isArray(closure) ? closure : []
  const constKeys = new Set((opts.constKeys || []).map((k) => String(k).trim().toUpperCase()))
  const outputVars = new Set((opts.outputVars || []).map((v) => String(v).trim().toLowerCase()))
  const fields = []
  const seen = new Set()
  const add = (key, fld) => { if (!seen.has(key)) { seen.add(key); fields.push(fld) } }

  const iterated = iteratedRoots(list)
  iterated.forEach((code) => {
    const rc = code.toUpperCase()
    add(`fd:${rc}`, { kind: 'formData', ruleCode: rc, model: `fd:${rc}`, label: `ข้อมูลรายการของ ${rc}` })
  })

  const defaultOption = (EXPENSE_40_5_OPTIONS.find((o) => o.isDefault) || EXPENSE_40_5_OPTIONS[0]).optionCode
  list.forEach((f) => {
    identifiersOf((f && f.expression) || '', opts.functionNames).forEach((id) => {
      const low = id.toLowerCase()
      if (outputVars.has(low)) return          // another formula computes it
      if (constKeys.has(id.toUpperCase())) return
      if (CATALOG_GLOBALS.has(low)) return     // the engine derives it
      if (PERSONAL_NUMERIC.has(low)) {
        add(`pers:${low}`, { kind: 'personal', key: low, model: `pers:${low}`, label: `${low} (personal)` })
        return
      }
      if (PERSONAL_NON_NUMERIC.has(low)) return
      const rate = RATE_BRIDGE_RE.exec(low)
      if (rate) {
        // Its rows each carry their own category, so a single row-level picker would assert ONE rate
        // for a portfolio the author is here to say has several.
        if (iterated.has(rate[1])) return
        const rc = rate[1].toUpperCase()
        add(`opt:${rc}`, {
          kind: 'option', ruleCode: rc, model: `opt:${rc}`, default: defaultOption,
          label: `ประเภททรัพย์สิน (เหมา) — ${rc}`,
        })
        return
      }
      if (iterated.has(low)) return // the bridge amount IS the sum of the rows below
      if (BRIDGE_NAME_RE.test(id)) {
        const rc = id.toUpperCase()
        add(`bridge:${rc}`, { kind: 'bridge', ruleCode: rc, model: `bridge:${rc}`, label: `จำนวนเงิน (${id})` })
      }
    })
  })
  return fields
}

// ──────────────────────────────────────────────────────────────────────────────
//  The NESTED rows a `[]` path walks
// ──────────────────────────────────────────────────────────────────────────────

/** One item inside a subItem — what `subItems[].items[].amount` reads. */
export function blankEntry() {
  return { labelTh: '', amount: 0 }
}

/** One subItem — one asset CATEGORY: it carries `subTypeCode` and therefore the `rate`. */
export function blankSubItem() {
  const def = (EXPENSE_40_5_OPTIONS.find((o) => o.isDefault) || EXPENSE_40_5_OPTIONS[0]).optionCode
  return { labelTh: '', subTypeCode: def, actualExpense: 0, items: [blankEntry()] }
}

/**
 * The เหมา rate a category carries, resolved the way the ENGINE resolves it: the version constant
 * `EXPENSE_RATE_<OPTION>` first, the shipped seed table only as a fallback.
 *
 * Reading the constant is what makes the preview honour a rate the admin edited on this very page
 * rather than quietly computing with the default — the two differing is precisely the case the
 * preview exists to catch.
 * @param {string} optionCode
 * @param {Array<{constKey?:string, constValue?:number}>} [constants]
 * @returns {number} 0 for หักตามจริง and for anything unresolvable
 */
export function rateFor(optionCode, constants) {
  const code = String(optionCode == null ? '' : optionCode).trim().toUpperCase()
  if (!code || code === ACTUAL_OPTION) return 0
  const konst = (Array.isArray(constants) ? constants : [])
    .find((c) => String((c && c.constKey) || '').trim().toUpperCase() === `EXPENSE_RATE_${code}`)
  if (konst && Number.isFinite(Number(konst.constValue))) return Number(konst.constValue)
  const opt = EXPENSE_40_5_OPTIONS.find((o) => o.optionCode === code)
  return opt && opt.rate != null ? opt.rate : 0
}

/** A subItem's own `amount` — the Σ of its entries, so `subItems[].amount` resolves too. */
export function subItemAmount(sub) {
  return entriesOf(sub).reduce((n, r) => {
    const v = Number(r && r.amount)
    return n + (Number.isFinite(v) ? v : 0)
  }, 0)
}

/** A subItem's entry list, defaulted (and materialized on the object) so the UI can bind to it. */
export function entriesOf(sub) {
  if (!sub) return []
  if (!Array.isArray(sub.items)) sub.items = [blankEntry()]
  return sub.items
}

/** The label a subItem falls back to — the same one the request sends, so the breakdown matches. */
export function subItemLabel(sub, idx) {
  const opt = EXPENSE_40_5_OPTIONS.find((o) => o.optionCode === (sub && sub.subTypeCode))
  return String((sub && sub.labelTh) || '').trim() || (opt && opt.label) || `หมวดที่ ${idx + 1}`
}

/** The label an entry falls back to. */
export function entryLabel(row, idx) {
  return String((row && row.labelTh) || '').trim() || `รายการที่ ${idx + 1}`
}

/**
 * ONE rule's nested `formData` block, in the shape `TaxCalculateRunRequest.FormData` documents
 * (specs/POC-Blueprint.md §5):
 *
 *   { value, expenseMode, actualExpense, subItems: [ { subTypeCode, labelTh, rate, amount,
 *                                                      actualExpense, items: [ {itemId, labelTh, amount} ] } ] }
 *
 * `value` and each subItem's `amount` are DERIVED sums rather than separate inputs: two places to type
 * the same number is two places for them to disagree, and the engine cross-checks `inputs.value`
 * against `formData.value` and warns when they differ.
 * @param {Array<Object>} rows       the subItem rows the panel collected
 * @param {Array<Object>} [constants] the version's constants (for rateFor)
 * @returns {Object}
 */
export function buildFormDataBlock(rows, constants) {
  const subItems = (Array.isArray(rows) ? rows : []).map((sub, si) => {
    const code = String((sub && sub.subTypeCode) || '').trim().toUpperCase()
    const actual = Number(sub && sub.actualExpense)
    return {
      subTypeCode: code,
      labelTh: subItemLabel(sub, si),
      rate: rateFor(code, constants),
      amount: subItemAmount(sub),
      // Only meaningful for a หักตามจริง category; sending it otherwise would leave a stale number in
      // the payload for rows the engine is going to charge a เหมา rate to.
      actualExpense: code === ACTUAL_OPTION && Number.isFinite(actual) ? actual : 0,
      items: entriesOf(sub).map((r, ri) => {
        const amount = Number(r && r.amount)
        return {
          // Sent so the returned breakdown can be keyed back to the row the user typed; the engine
          // invents one when it is blank, so this only makes that mapping ours.
          itemId: `s${si + 1}-${ri + 1}`,
          labelTh: entryLabel(r, ri),
          amount: Number.isFinite(amount) ? amount : 0,
        }
      }),
    }
  })
  return {
    value: subItems.reduce((n, x) => n + x.amount, 0),
    expenseMode: subItems.length && subItems.every((x) => x.subTypeCode === ACTUAL_OPTION) ? 'ตามจริง' : 'เหมา',
    actualExpense: subItems.reduce((n, x) => n + x.actualExpense, 0),
    subItems,
  }
}

/**
 * Turn the panel's collected values into the request body.
 *
 * `formData` is ALWAYS present (`{}` when there is nothing nested). Sending it is the whole point:
 * without it the engine projects the flat `inputs` rows into this same structure and attaches a
 * permanent "ส่งมาในรูปแบบเดิม … ตรวจสอบผลลัพธ์ก่อนใช้งาน" warning — on completely healthy input, in the
 * same list as the fall-through warning that actually means something. A warning list where one entry
 * is always present is a warning list nobody reads.
 * @param {Array<Object>} fields  from {@link derivePreviewFields}
 * @param {Object} values         model → value, keyed by `field.model`
 * @param {Array<Object>} [constants]
 * @returns {{personalInfo:Object, inputs:Array<Object>, formData:Object}}
 */
export function buildPreviewPayload(fields, values, constants) {
  const inputByRule = {}
  const personalInfo = {}
  const formData = {}
  const vals = values || {}
  ;(Array.isArray(fields) ? fields : []).forEach((fld) => {
    if (!fld) return
    const raw = vals[fld.model]
    const key = String(fld.ruleCode || '').toUpperCase()
    if (fld.kind === 'bridge') {
      const v = Number(raw)
      inputByRule[key] = { ...(inputByRule[key] || { ruleCode: fld.ruleCode }), value: Number.isFinite(v) ? v : 0 }
    } else if (fld.kind === 'option') {
      inputByRule[key] = { ...(inputByRule[key] || { ruleCode: fld.ruleCode }), expenseOption: raw || '' }
    } else if (fld.kind === 'formData') {
      const block = buildFormDataBlock(Array.isArray(raw) ? raw : [], constants)
      formData[key] = block
      // The scalar bridge var `<rule_code>` is still what every non-path formula in the closure reads,
      // so the row carries the same total the block does. Sending BOTH is safe only because both come
      // from ONE derivation — the engine warns when inputs.value and formData.value disagree.
      inputByRule[key] = { ...(inputByRule[key] || { ruleCode: fld.ruleCode }), value: block.value }
    } else if (fld.kind === 'personal') {
      const v = Number(raw)
      personalInfo[fld.key] = Number.isFinite(v) ? v : 0
    }
  })
  return { personalInfo, inputs: Object.values(inputByRule), formData }
}

export default {
  ACTUAL_OPTION,
  identifiersOf,
  iteratedRoots,
  derivePreviewFields,
  blankEntry,
  blankSubItem,
  entriesOf,
  rateFor,
  subItemAmount,
  subItemLabel,
  entryLabel,
  buildFormDataBlock,
  buildPreviewPayload,
}
